import { useState, useRef, useEffect } from "react"
import { ChevronDown, Copy, Check, X, Users } from "lucide-react"
import CodeMirror from "@uiw/react-codemirror"
import { cpp } from "@codemirror/lang-cpp"
import { javascript } from "@codemirror/lang-javascript"
import { python } from "@codemirror/lang-python"
import { rust } from "@codemirror/lang-rust"
import { php } from "@codemirror/lang-php"
import { EditorView } from "@codemirror/view"
import { db } from "../services/firebase"
import {
  doc,
  getDoc,
  setDoc,
  onSnapshot,
  updateDoc,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore"
import { useAuth } from "../contexts/AuthProvider"

// ─── Language config ──────────────────────────────────────────────────────────
const LANGUAGES = [
  {
    id: "cpp",
    label: "C++",
    extension: cpp,
    starter: `#include <iostream>\n#include <string>\nusing namespace std;\n\nint main() {\n    \n    return 0;\n}\n`,
  },
  {
    id: "c",
    label: "C",
    extension: cpp,
    starter: `#include <stdio.h>\n\nint main() {\n    \n    return 0;\n}\n`,
  },
  {
    id: "python",
    label: "Python",
    extension: python,
    starter: `def solution():\n    pass\n`,
  },
  {
    id: "javascript",
    label: "JavaScript",
    extension: () => javascript({ jsx: false }),
    starter: `function solution() {\n    \n}\n`,
  },
  {
    id: "typescript",
    label: "TypeScript",
    extension: () => javascript({ typescript: true }),
    starter: `function solution(): void {\n    \n}\n`,
  },
  {
    id: "rust",
    label: "Rust",
    extension: rust,
    starter: `fn main() {\n    \n}\n`,
  },
  {
    id: "php",
    label: "PHP",
    extension: php,
    starter: `<?php\n\nfunction solution() {\n    \n}\n`,
  },
  {
    id: "text",
    label: "Text",
    extension: null,
    starter: `Think out loud here — describe your approach in plain English.\n`,
  },
]

// ─── Main component ───────────────────────────────────────────────────────────
const CompilerPage = ({ meetingId, onClose }) => {
  const { user } = useAuth()

  const initialLang = LANGUAGES[0]
  const [selectedLang, setSelectedLang] = useState(initialLang)
  const [code, setCode] = useState(initialLang.starter)
  const [question, setQuestion] = useState("")
  const [langMenuOpen, setLangMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [activeUsers, setActiveUsers] = useState([])
  const [isSyncing, setIsSyncing] = useState(false)
  const [lastSyncTime, setLastSyncTime] = useState(null)

  const langMenuRef = useRef(null)
  const editorRef = useRef(null)
  const codeDocRef = useRef(null)
  const unsubscribeRef = useRef(null)
  const isInitialLoadRef = useRef(true)
  const isSyncingRef = useRef(false)
  const lastRemoteCodeRef = useRef("")
  const codeChangeTimeoutRef = useRef(null)

  // ── Close on Escape ──────────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose?.()
    }
    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [onClose])

  // ── Lang menu outside click ──────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target))
        setLangMenuOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  // ── Firebase setup ───────────────────────────────────────────────────────
  useEffect(() => {
    if (!meetingId) return
    codeDocRef.current = doc(db, "rooms", meetingId, "code", "editor")
  }, [meetingId])

  useEffect(() => {
    if (!meetingId || !codeDocRef.current) return

    const init = async () => {
      try {
        const docSnap = await getDoc(codeDocRef.current)

        if (!docSnap.exists()) {
          lastRemoteCodeRef.current = initialLang.starter
          await setDoc(codeDocRef.current, {
            language: initialLang.id,
            code: initialLang.starter,
            question: "",
            lastUpdated: serverTimestamp(),
            activeUsers: user?.uid ? [user.uid] : [],
          })
        } else {
          if (user?.uid) {
            await updateDoc(codeDocRef.current, {
              activeUsers: arrayUnion(user.uid),
            })
          }
        }

        const unsubscribe = onSnapshot(codeDocRef.current, (snapshot) => {
          if (!snapshot.exists()) return
          const data = snapshot.data()

          isSyncingRef.current = true

          if (data.language) {
            const lang = LANGUAGES.find((l) => l.id === data.language)
            if (lang && lang.id !== selectedLang.id) {
              setSelectedLang(lang)
            }
          }

          if (data.code !== undefined) {
            if (isInitialLoadRef.current) {
              lastRemoteCodeRef.current = data.code
              setCode(data.code)
            } else if (
              data.code !== code &&
              data.code !== lastRemoteCodeRef.current
            ) {
              lastRemoteCodeRef.current = data.code
              setCode(data.code)
            }
          }

          if (data.question !== undefined) setQuestion(data.question)
          if (data.activeUsers) setActiveUsers(data.activeUsers)
          setLastSyncTime(new Date().toISOString())

          setTimeout(() => {
            isSyncingRef.current = false
            isInitialLoadRef.current = false
          }, 100)
        })

        unsubscribeRef.current = unsubscribe
      } catch (err) {
        console.error("Firebase init error:", err)
      }
    }

    init()

    return () => {
      unsubscribeRef.current?.()
      if (user?.uid && codeDocRef.current) {
        updateDoc(codeDocRef.current, {
          activeUsers: arrayRemove(user.uid),
        }).catch(console.error)
      }
    }
  }, [meetingId, user])

  // ── Sync code to Firebase ────────────────────────────────────────────────
  useEffect(() => {
    if (isInitialLoadRef.current || isSyncingRef.current) return
    clearTimeout(codeChangeTimeoutRef.current)
    codeChangeTimeoutRef.current = setTimeout(() => {
      syncCode(selectedLang.id, code)
    }, 500)
    return () => clearTimeout(codeChangeTimeoutRef.current)
  }, [code, selectedLang.id])

  useEffect(() => {
    if (isInitialLoadRef.current || isSyncingRef.current) return
    const id = setTimeout(() => {
      if (!codeDocRef.current) return
      updateDoc(codeDocRef.current, {
        question,
        lastUpdated: serverTimestamp(),
      }).catch(console.error)
    }, 1000)
    return () => clearTimeout(id)
  }, [question])

  const syncCode = async (langId, codeValue) => {
    if (!codeDocRef.current || isSyncingRef.current) return
    try {
      setIsSyncing(true)
      lastRemoteCodeRef.current = codeValue
      await updateDoc(codeDocRef.current, {
        language: langId,
        code: codeValue,
        lastUpdated: serverTimestamp(),
      })
      setLastSyncTime(new Date().toISOString())
    } catch (err) {
      console.error("Sync error:", err)
    } finally {
      setIsSyncing(false)
    }
  }

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleLangSelect = (lang) => {
    setSelectedLang(lang)
    setCode(lang.starter)
    setLangMenuOpen(false)
    syncCode(lang.id, lang.starter)
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(code)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  // ── Editor theme (matches CollaborativeEditor) ───────────────────────────
  const monoTheme = EditorView.theme({
    "&": { background: "#fafafa !important", height: "100%", width: "100%" },
    ".cm-content": { padding: "16px 0", color: "#1c1c1c" },
    ".cm-gutters": {
      background: "#f4f4f4 !important",
      borderRight: "1px solid #e4e4e4",
      color: "#aaa",
      minWidth: "44px",
    },
    ".cm-lineNumbers .cm-gutterElement": { padding: "0 10px 0 4px" },
    ".cm-activeLineGutter": { background: "rgba(0,0,0,0.04) !important" },
    ".cm-activeLine": { background: "rgba(0,0,0,0.02) !important" },
    ".cm-cursor": { borderLeftColor: "#1c1c1c", borderLeftWidth: "2px" },
    ".cm-selectionBackground, .cm-focused .cm-selectionBackground": {
      background: "rgba(0,0,0,0.08) !important",
    },
    ".cm-editor": { height: "100%", width: "100%" },
    ".cm-scroller": {
      overflow: "auto",
      height: "100%",
      fontFamily: "'IBM Plex Mono', 'JetBrains Mono', monospace",
      fontSize: "13px",
      lineHeight: "1.7",
    },
    ".tok-keyword": { color: "#7c3aed" },
    ".tok-string": { color: "#16a34a" },
    ".tok-number": { color: "#ea580c" },
    ".tok-comment": { color: "#aaa", fontStyle: "italic" },
    ".tok-typeName": { color: "#0369a1" },
    ".tok-function": { color: "#1d4ed8" },
    ".tok-operator": { color: "#666" },
  })

  const extensions = [
    monoTheme,
    EditorView.lineWrapping,
    ...(selectedLang.extension
      ? [
          typeof selectedLang.extension === "function" &&
          selectedLang.extension.length === 0
            ? selectedLang.extension()
            : selectedLang.extension(),
        ]
      : []),
  ]

  return (
    <div
      className="flex h-full bg-white overflow-hidden"
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Serif:ital,wght@0,400;0,500;1,400&display=swap');
        * { box-sizing: border-box; }
        .compiler-hide-scroll::-webkit-scrollbar { display: none; }
        .compiler-hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      {/* ── Left panel: Question (50%) ─────────────────────────────────── */}
      <div
        className="flex flex-col border-r border-zinc-300"
        style={{ width: "50%", minWidth: 0, flexShrink: 0 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-300 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
              Question
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Active users badge */}
            {activeUsers.length > 0 && (
              <div className="flex items-center gap-1.5 text-xs text-zinc-400">
                <Users size={12} />
                <span
                  style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                  className="font-medium"
                >
                  {activeUsers.length}
                </span>
              </div>
            )}

            {/* Sync indicator */}
            {lastSyncTime && (
              <span
                className="text-xs font-medium"
                style={{
                  color: isSyncing ? "#d97706" : "#16a34a",
                  fontFamily: "'IBM Plex Mono', monospace",
                }}
              >
                {isSyncing ? "syncing" : "synced"}
              </span>
            )}
          </div>
        </div>

        {/* Question textarea */}
        <textarea
          className="flex-grow resize-none px-5 py-5 outline-none compiler-hide-scroll bg-white text-zinc-700"
          style={{
            fontFamily: "'IBM Plex Serif', Georgia, serif",
            fontSize: "14px",
            lineHeight: "1.8",
            border: "none",
          }}
          placeholder="Paste the interview question or problem statement here..."
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
        />
      </div>

      {/* ── Right panel: Code Editor (50%) ────────────────────────────── */}
      <div
        className="flex flex-col"
        style={{ width: "50%", minWidth: 0, flexShrink: 0 }}
      >
        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-300 bg-white shrink-0">
          <span className="text-xs font-bold uppercase tracking-widest text-zinc-400">
            Solution
          </span>

          <div className="flex items-center gap-2">
            {/* Copy button */}
            <button
              onClick={handleCopy}
              className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 bg-white hover:bg-zinc-50 border border-zinc-300 hover:border-zinc-900 px-3 py-1.5 transition-all"
              style={{ borderRadius: 0 }}
            >
              {copied ? (
                <>
                  <Check size={11} className="text-emerald-600" />
                  Copied
                </>
              ) : (
                <>
                  <Copy size={11} />
                  Copy
                </>
              )}
            </button>

            {/* Language picker */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen((p) => !p)}
                className="flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-900 px-3 py-1.5 transition-colors"
                style={{ borderRadius: 0 }}
              >
                <span className="text-white text-xs font-bold tracking-wide">
                  {selectedLang.label}
                </span>
                <ChevronDown
                  size={11}
                  className={`text-zinc-400 transition-transform duration-150 ${langMenuOpen ? "rotate-180" : ""}`}
                />
              </button>

              {langMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-0 bg-white border border-zinc-300 overflow-hidden z-50 min-w-[140px]"
                  style={{ borderRadius: 0, boxShadow: "4px 4px 0 #1c1c1c" }}
                >
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => handleLangSelect(lang)}
                      className={`w-full text-left px-4 py-2.5 text-xs font-semibold transition-colors border-b border-zinc-100 last:border-0
                        ${
                          selectedLang.id === lang.id
                            ? "bg-zinc-900 text-white"
                            : "text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
                        }`}
                      style={{ borderRadius: 0 }}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Close button */}
            {onClose && (
              <button
                onClick={onClose}
                className="p-1 hover:bg-zinc-100 text-zinc-400 hover:text-zinc-800 transition-colors"
                style={{ borderRadius: 0 }}
                title="Close (Esc)"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Editor area */}
        <div
          className="flex-1 overflow-hidden bg-zinc-50"
          style={{ position: "relative" }}
        >
          {selectedLang.value === "text" || selectedLang.id === "text" ? (
            <textarea
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="bg-zinc-50 text-zinc-800 px-5 py-4 outline-none resize-none leading-relaxed"
              style={{
                fontFamily: "'IBM Plex Serif', Georgia, serif",
                fontSize: "14px",
                lineHeight: "1.8",
                borderRadius: 0,
                border: "none",
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
              placeholder="Think out loud — describe your approach in plain English..."
              spellCheck={true}
            />
          ) : (
            <CodeMirror
              ref={editorRef}
              value={code}
              extensions={extensions}
              onChange={setCode}
              basicSetup={{
                lineNumbers: true,
                highlightActiveLineGutter: true,
                highlightSpecialChars: true,
                foldGutter: false,
                drawSelection: true,
                dropCursor: false,
                allowMultipleSelections: false,
                indentOnInput: true,
                syntaxHighlighting: true,
                bracketMatching: true,
                closeBrackets: true,
                autocompletion: true,
                rectangularSelection: false,
                crosshairCursor: false,
                highlightActiveLine: true,
                highlightSelectionMatches: false,
                closeBracketsKeymap: true,
                searchKeymap: false,
                foldKeymap: false,
                completionKeymap: true,
                lintKeymap: false,
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default CompilerPage
